import { useEffect, useState } from 'react';
import { IState } from '../../redux/redux';
import Navigation from '../Navigation/navigation';
import { EasyImage } from '../Tools/EasyImage';
import StoreX from '../../redux/oldStore';

export type HeaderLayout = 'stacked' | 'bar' | 'banner' | 'split';

/** Missing, 0, and unknown values stay on the header that ships today. */
export function headerLayoutOf(value?: string | null): HeaderLayout {
  switch ((value ?? '').toLowerCase()) {
    case 'bar':
    case '1':
      return 'bar';
    case 'banner':
    case '2':
      return 'banner';
    case 'split':
    case '3':
      return 'split';
    default:
      return 'stacked';
  }
}

const NHD_LOGO = '../api/FileApi/FileFetcherClient/e99bb006-500d-40fb-82df-6946fe3e383a';
const NHD_MARK = 'https://zfairs.com/file/NHD.svg';

function httpUrl(value?: string | null): string | null {
  const trimmed = (value ?? '').trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return null;
}

function cssImageUrl(value?: string | null): string | null {
  const trimmed = httpUrl(value);
  if (!trimmed || /["'()\\\s]/.test(trimmed)) return null;
  return trimmed;
}

interface IProps {
  state: IState;
  useOld: boolean;
}

const Header = (props: IProps) => {
  const [ready, setReady] = useState<boolean>(false);
  const [headingStyle, setHeadingStyle] = useState<'std' | 'nhd'>('std');

  useEffect(() => {
    if (props.state.SettingsResponse) {
      setReady(true);

      if (props.state.SettingsResponse.Settings.isHistoryContest) {
        setHeadingStyle('nhd');
      } else {
        setHeadingStyle('std');
      }
    }
  }, [props.state]);

  const response = props.state.SettingsResponse;
  const name = response?.Name ?? '';
  const home = StoreX.BuildUrl('../');
  const logoSrc = response?.Logo ?? '';
  const showNhd = !!props.state.Settings?.isHistoryContest;
  const bannerImage = cssImageUrl(props.state.Settings?.headerBannerImage);
  const partnerLogo = cssImageUrl(props.state.Settings?.headerPartnerLogo);
  const partnerLink = httpUrl(props.state.Settings?.headerPartnerUrl);
  const primaryCaption = (props.state.Settings?.headerPrimaryCaption ?? '').trim();
  const partnerCaption = (props.state.Settings?.headerPartnerCaption ?? '').trim();
  const tagline = (props.state.Settings?.headerTagline ?? '').trim();
  const partner = partnerLogo
    ? { src: partnerLogo, href: partnerLink, alt: 'Partner logo' }
    : showNhd
      ? { src: NHD_MARK, href: 'https://nhd.org', alt: 'NHD logo' }
      : null;

  const logoMark = (alt: string, linked: boolean) =>
    logoSrc.length > 0 ? (
      <div className="x-logo">
        {linked ? (
          <a href={home}>
            <EasyImage src={logoSrc} alt={alt} />
          </a>
        ) : (
          <EasyImage src={logoSrc} alt={alt} />
        )}
      </div>
    ) : null;

  const imageMark = (src: string, alt: string, href: string | null) => (
    <div className="x-logo">
      {href ? (
        <a href={href}>
          <EasyImage src={src} alt={alt} />
        </a>
      ) : (
        <EasyImage src={src} alt={alt} />
      )}
    </div>
  );

  const taglineLine = tagline ? <p className="x-header-tagline">{tagline}</p> : null;

  const renderNewHeader = () => {
    const layout = headerLayoutOf(props.state.Settings?.headerLayout);
    if (layout === 'bar') {
      return (
        <div className="x-container">
          <div className="x-header-row">
            <a className="x-header-brand" href={home}>
              {logoMark('logo', false)}
              <h1 className="x-header-name">{name}</h1>
            </a>
            <div className="x-menu">
              <Navigation useOld={false} />
            </div>
          </div>
        </div>
      );
    }
    if (layout === 'banner') {
      return (
        <>
          <div className="x-banner-stage">
            {bannerImage ? (
              <div className="x-banner-photo" style={{ backgroundImage: `url("${bannerImage}")` }} />
            ) : null}
            <div className="x-container">
              <div className="x-banner-copy">
                {logoMark('logo', true)}
                <div className="x-banner-text">
                  <h1 className="x-header-name">{name}</h1>
                  {taglineLine}
                </div>
              </div>
            </div>
          </div>
          <div className="x-banner-menu">
            <div className="x-container">
              <div className="x-menu">
                <Navigation useOld={false} />
              </div>
            </div>
          </div>
        </>
      );
    }
    if (layout === 'split') {
      return (
        <>
          <div className="x-split-top">
            <div className="x-container x-split-grid">
              <div className="x-split-side">
                {logoSrc && primaryCaption ? <span className="x-split-caption">{primaryCaption}</span> : null}
                {logoMark('logo', true)}
              </div>
              <div className="x-split-copy">
                <h1 className="x-header-name">{name}</h1>
                {taglineLine}
              </div>
              <div className="x-split-side">
                {partner && partnerCaption ? <span className="x-split-caption">{partnerCaption}</span> : null}
                {partner ? imageMark(partner.src, partner.alt, partner.href) : null}
              </div>
            </div>
          </div>
          <div className="x-split-menu">
            <div className="x-container">
              <div className="x-menu">
                <Navigation useOld={false} />
              </div>
            </div>
          </div>
        </>
      );
    }
    return (
      <div className="x-container">
        <div className="">
          <div className="x-logo-">
            {/* <a href={StoreX.BuildUrl('../')}>
              <EasyImage src={props.state.SettingsResponse.Logo} alt="logo" />
            </a> */}
          </div>
          <div className="x-menu">
            <Navigation useOld={props.useOld} />
          </div>
        </div>
        <div className="x-title">
          <div className="x-logo">
            <a href={home}>
              <EasyImage src={logoSrc} alt="logo" />
            </a>
          </div>
          <h1>{name}</h1>
        </div>
      </div>
    );
  };

  return (
    <>
      {props.state.SettingsResponse && (
        <>
          {props.useOld ? (
            <>
              {ready && headingStyle === 'std' && (
                <>
                  <div className="title-bar primary-bg no-print">
                    {props.state.SettingsResponse && props.state.SettingsResponse.Logo?.length > 0 && (
                      <div className="logo">
                        <EasyImage src={props.state.SettingsResponse.Logo} alt="logo" link={StoreX.BuildUrl(``)} />
                      </div>
                    )}
                    <div>{props.state.SettingsResponse && <h1>{props.state.SettingsResponse.Name}</h1>}</div>
                    <div className="logo"></div>
                  </div>
                  <Navigation useOld={props.useOld} />
                </>
              )}

              {ready && headingStyle === 'nhd' && (
                <>
                  <div className="title-bar primary-bg no-print">
                    <div className="logo nhd-logo">
                      <EasyImage src={StoreX.BuildUrl(NHD_LOGO)} alt="nhd logo" />
                    </div>
                    <div>{props.state.SettingsResponse && <h1>{props.state.SettingsResponse.Name}</h1>}</div>
                    {props.state.SettingsResponse && props.state.SettingsResponse.Logo?.length > 0 && (
                      <div className="logo">
                        <EasyImage src={props.state.SettingsResponse.Logo} alt="logo" />
                      </div>
                    )}
                  </div>
                  <Navigation  useOld={props.useOld} />
                </>
              )}
            </>
          ) : (
            renderNewHeader()
          )}
        </>
      )}
    </>
  );
};

export default Header;
